import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Utensils, ArrowLeft, Lock, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const L = {
  es: {
    title: 'Planes nutricionales', back: 'Volver a Entrenamiento',
    premiumOnly: 'Solo para usuarios Premium.', upgrade: 'Mejorar a Premium',
    noPlans: 'Necesitas un plan de entrenamiento. Créalo primero.', goPlans: 'Ir a mis planes',
    generate: 'Generar plan nutricional', name: 'Nombre (opcional)', namePh: 'Ej: Nutrición volumen',
    trainingPlan: 'Plan de entrenamiento', mode: 'Modo', meals: 'Comidas', snacks: 'Snacks por día',
    create: 'Generar', creating: 'Generando...', myPlans: 'Mis planes nutricionales', empty: 'Aún no tienes planes nutricionales.',
    open: 'Abrir', weeksLabel: 'semanas', quota: 'Menús IA este mes', deleteConfirm: '¿Borrar este plan nutricional?',
    breakfast: 'Desayuno', lunch: 'Comida', dinner: 'Cena', snack: 'Snack (cada uno)',
    modes: { recipes: 'Solo mis recetas', ai: 'IA desde cero', mix: 'Mezcla' },
    splitTitle: 'Reparto de calorías por comida (%)',
    splitHint: 'Indica qué porcentaje de las calorías y macros del día va a cada comida. Se usa para elegir, de tu inventario, la receta que mejor encaja en cada hueco. No hace falta que sume 100% exacto (se ajusta solo).',
    splitTotal: 'Total', splitOnlyRecipes: 'Solo aplica a los modos «Solo mis recetas» y «Mezcla».',
  },
  en: {
    title: 'Nutrition plans', back: 'Back to Training',
    premiumOnly: 'Premium users only.', upgrade: 'Upgrade to Premium',
    noPlans: 'You need a training plan first.', goPlans: 'Go to my plans',
    generate: 'Generate nutrition plan', name: 'Name (optional)', namePh: 'E.g.: Bulk nutrition',
    trainingPlan: 'Training plan', mode: 'Mode', meals: 'Meals', snacks: 'Snacks per day',
    create: 'Generate', creating: 'Generating...', myPlans: 'My nutrition plans', empty: 'No nutrition plans yet.',
    open: 'Open', weeksLabel: 'weeks', quota: 'AI menus this month', deleteConfirm: 'Delete this nutrition plan?',
    breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack (each)',
    modes: { recipes: 'My recipes only', ai: 'AI from scratch', mix: 'Mix' },
    splitTitle: 'Calorie split per meal (%)',
    splitHint: 'Sets what percentage of the day’s calories and macros goes to each meal. Used to pick, from your inventory, the recipe that best fits each slot. It doesn’t need to add up to exactly 100% (it’s auto-adjusted).',
    splitTotal: 'Total', splitOnlyRecipes: 'Only applies to the “My recipes only” and “Mix” modes.',
  },
};

export default function NutritionPlansPage() {
  const { language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = L[language] || L.es;
  const isPremium = user?.plan === 'premium';

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [trainingPlans, setTrainingPlans] = useState([]);
  const [quota, setQuota] = useState(null);
  const [generating, setGenerating] = useState(false);

  const [form, setForm] = useState({
    name: '', training_plan_id: '', mode: 'ai',
    include_breakfast: true, include_lunch: true, include_dinner: true, snacks_per_day: 1,
    meal_split: { breakfast: 25, lunch: 35, dinner: 30, snack: 10 },
  });

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    Promise.all([fetchPlans(), fetchTrainingPlans()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/nutrition/plans`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setPlans(d.plans || []); setQuota(d.quota || null); }
    } catch (e) { console.error(e); }
  };

  const fetchTrainingPlans = async () => {
    try {
      const res = await fetch(`${API}/training/plans`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const tp = d.plans || [];
        setTrainingPlans(tp);
        const preset = searchParams.get('training_plan_id');
        setForm(f => ({ ...f, training_plan_id: preset || (tp[0]?.plan_id || '') }));
      }
    } catch (e) { console.error(e); }
  };

  const handleGenerate = async () => {
    if (!form.training_plan_id) { toast.error(language === 'es' ? 'Elige un plan de entrenamiento' : 'Pick a training plan'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/nutrition/plans/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          name: form.name || null,
          training_plan_id: form.training_plan_id,
          mode: form.mode,
          include_breakfast: form.include_breakfast,
          include_lunch: form.include_lunch,
          include_dinner: form.include_dinner,
          snacks_per_day: parseInt(form.snacks_per_day) || 0,
          meal_split: form.meal_split,
        }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(language === 'es' ? 'Plan nutricional generado' : 'Nutrition plan generated'); navigate(`/nutrition/plans/${data.plan_id}`); }
      else { toast.error(data.detail || (language === 'es' ? 'No se pudo generar' : 'Could not generate')); }
    } catch { toast.error(language === 'es' ? 'Error de conexión' : 'Connection error'); }
    finally { setGenerating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      const res = await fetch(`${API}/nutrition/plans/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) setPlans(prev => prev.filter(p => p.plan_id !== id));
    } catch { toast.error('Error'); }
  };

  if (loading) return <Layout><div className="max-w-4xl mx-auto px-4 py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8 text-primary" /></div>
          <h1 className="font-manrope font-bold text-2xl text-charcoal mb-2">{t.title}</h1>
          <p className="text-stone font-inter mb-6">{t.premiumOnly}</p>
          <Button onClick={() => navigate('/pricing')} className="rounded-full bg-primary">{t.upgrade}</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/training')} className="flex items-center gap-2 text-stone hover:text-primary mb-4 font-inter">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </button>
        <div className="flex items-center gap-3 mb-6">
          <Utensils className="w-7 h-7 text-primary" />
          <h1 className="font-manrope font-bold text-3xl text-charcoal">{t.title}</h1>
        </div>

        {trainingPlans.length === 0 ? (
          <Card className="bg-white rounded-3xl shadow-soft p-6 text-center">
            <p className="text-stone font-inter mb-4">{t.noPlans}</p>
            <Button onClick={() => navigate('/training/plans')} className="rounded-full bg-primary">{t.goPlans}</Button>
          </Card>
        ) : (
          <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-4 mb-8">
            <h2 className="font-manrope font-bold text-lg text-charcoal">{t.generate}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.name}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.namePh} className="h-11 rounded-xl border-border bg-white/50" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.trainingPlan}</Label>
                <select value={form.training_plan_id} onChange={(e) => setForm({ ...form, training_plan_id: e.target.value })} className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm">
                  {trainingPlans.map(tp => <option key={tp.plan_id} value={tp.plan_id}>{tp.name} ({tp.weeks} {t.weeksLabel})</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.mode}</Label>
                <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm">
                  {['ai', 'recipes', 'mix'].map(m => <option key={m} value={m}>{t.modes[m]}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-stone">{t.snacks}</Label>
                <Input type="number" min="0" max="4" value={form.snacks_per_day} onChange={(e) => setForm({ ...form, snacks_per_day: e.target.value })} className="h-11 rounded-xl border-border bg-white/50" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-stone">{t.meals}</Label>
              <div className="flex flex-wrap gap-2">
                {[['include_breakfast', t.breakfast], ['include_lunch', t.lunch], ['include_dinner', t.dinner]].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setForm({ ...form, [key]: !form[key] })}
                    className={`px-4 py-2 rounded-full text-sm border-2 transition-all ${form[key] ? 'bg-primary text-white border-primary' : 'bg-white text-stone border-border'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Meal split (only relevant when recipes are used) */}
            {form.mode !== 'ai' && (() => {
              const active = [
                form.include_breakfast && ['breakfast', t.breakfast],
                form.include_lunch && ['lunch', t.lunch],
                form.include_dinner && ['dinner', t.dinner],
                (parseInt(form.snacks_per_day) || 0) > 0 && ['snack', t.snack],
              ].filter(Boolean);
              const total = active.reduce((s, [k]) => s + (parseFloat(form.meal_split[k]) || 0), 0);
              return (
                <div className="space-y-2 border-t border-border/60 pt-4">
                  <Label className="text-sm font-semibold text-charcoal">{t.splitTitle}</Label>
                  <p className="text-xs text-stone font-inter">{t.splitHint}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {active.map(([key, label]) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs text-stone">{label}</Label>
                        <div className="relative">
                          <Input
                            type="number" min="0" max="100" inputMode="numeric"
                            value={form.meal_split[key]}
                            onChange={(e) => setForm({ ...form, meal_split: { ...form.meal_split, [key]: e.target.value } })}
                            className="h-10 rounded-xl border-border bg-white/50 pr-7 text-sm"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone text-sm">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone">{t.splitTotal}: <b className={Math.round(total) === 100 ? 'text-sage' : 'text-stone'}>{Math.round(total)}%</b></p>
                </div>
              );
            })()}
            {form.mode === 'ai' && (
              <p className="text-xs text-stone/70 italic">{t.splitOnlyRecipes}</p>
            )}
            {quota && quota.limit != null && (
              <p className="text-xs text-stone">{t.quota}: {quota.used} / {quota.limit}</p>
            )}
            <Button onClick={handleGenerate} disabled={generating} className="rounded-full bg-primary h-12 w-full font-manrope font-semibold">
              {generating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-2" />}
              {generating ? t.creating : t.create}
            </Button>
          </Card>
        )}

        <h2 className="font-manrope font-bold text-lg text-charcoal mb-3">{t.myPlans}</h2>
        {plans.length === 0 ? (
          <p className="text-stone italic font-inter">{t.empty}</p>
        ) : (
          <div className="space-y-3">
            {plans.map(p => (
              <Card key={p.plan_id} className="bg-white rounded-2xl shadow-soft p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><Utensils className="w-6 h-6 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-manrope font-semibold text-charcoal truncate">{p.name}</p>
                  <p className="text-xs text-stone">{p.weeks} {t.weeksLabel} · {t.modes[p.mode] || p.mode}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate(`/nutrition/plans/${p.plan_id}`)} className="rounded-full">{t.open}</Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(p.plan_id)} className="rounded-full text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
